import { BusEvent, HpStep } from '@/helper/Types'
import StepModel from '@/models/StepModel'
import bus from '@/helper/Bus'
import i18n from '@/helper/Translations'
import { reactive } from 'vue'

class AppStore {
  private _projectSorting: { column: string; reversed: boolean } = { column: '', reversed: true }
  private _currentStepValid = false
  private _currentFormValid = false
  private _currentStep: HpStep = HpStep.EMPTY
  private _isLoading = 0
  private _stepsMap: { [key: string]: number } = {}
  private _currentStepComponent!: any
  private _errorLoadingProject!: boolean
  private _steps: StepModel[] = []

  private _stepConditions: { [key: string]: boolean } = {
    includeSaleSet: false
  }

  private _stepConfigs: StepModel[] = [
    {
      name: 'steps.project_details',
      title: 'step_details.title',
      step: HpStep.PROJECT_DETAILS,
      isValid: false,
      defaultNextValidity: true,
      tag: 'project_details'
    },
    {
      name: 'steps.heating_details',
      title: 'step_heating_details.title',
      step: HpStep.PLANNING,
      isValid: false,
      defaultNextValidity: true,
      tag: 'heating_details'
    },
    {
      name: 'steps.heat_pump_system',
      title: 'step_heat_pumps.title',
      step: HpStep.HEAT_PUMP_SYSTEM,
      isValid: false,
      defaultNextValidity: false,
      tag: 'heat_pump'
    },
    {
      name: 'steps.sale_set',
      title: 'step_sale_set.title',
      step: HpStep.SALE_SET,
      isValid: false,
      defaultNextValidity: false,
      tag: 'sale_set'
    },
    {
      name: 'steps.components',
      title: 'step_components.title',
      step: HpStep.COMPONENTS,
      isValid: false,
      defaultNextValidity: false,
      tag: 'components'
    },
    {
      name: 'steps.installation',
      title: 'step_installation.title',
      step: HpStep.INSTALLATION,
      isValid: false,
      defaultNextValidity: true,
      tag: 'installation'
    },
    {
      name: 'steps.system_controllers',
      title: 'step_system_controllers.title',
      step: HpStep.SYSTEM_CONTROLLERS,
      isValid: false,
      defaultNextValidity: false,
      tag: 'system_controllers'
    },
    {
      name: 'steps.accessories',
      title: 'step_accessories.title',
      step: HpStep.ACCESSORIES,
      isValid: false,
      defaultNextValidity: true,
      tag: 'accessories'
    },
    {
      name: 'steps.system_schemes',
      title: 'step_system_schemes.title',
      step: HpStep.SYSTEM_SCHEMES,
      isValid: false,
      defaultNextValidity: true,
      tag: 'system_schemes'
    },
    {
      name: 'steps.summary',
      title: 'step_summary.title',
      step: HpStep.SUMMARY,
      isValid: false,
      defaultNextValidity: true,
      tag: 'summary'
    }
  ]

  constructor() {
    this.refreshVisibleSteps()
  }

  // Dynamically build steps based on conditions
  public refreshVisibleSteps() {
    const shouldInclude = (step: StepModel): boolean => {
      if (step.step === HpStep.SALE_SET) {
        return this._stepConditions.includeSaleSet
      }
      return true
    }

    const existingValidity: { [key: string]: boolean } = {}
    this._steps.forEach(step => {
      existingValidity[step.step.toString()] = step.isValid
    })

    this._steps = this._stepConfigs.filter(shouldInclude).map(step => ({
      ...step,
      isValid: existingValidity[step.step.toString()] ?? false
    }))

    this._stepsMap = {}
    this._steps.forEach((step, index) => {
      this._stepsMap[step.step.toString()] = index
    })

    if (!this._steps.find(s => s.step === this._currentStep)) {
      this._currentStep = this._steps[0]?.step ?? HpStep.EMPTY
    }
  }

  public setStepCondition(key: string, value: boolean) {
    if (this._stepConditions[key] !== value) {
      this._stepConditions[key] = value
      this.refreshVisibleSteps()
    }
  }

  get isLoading(): boolean {
    return !!this._isLoading
  }

  set isLoading(value: boolean) {
    if (value) {
      this._isLoading++
    } else if (this._isLoading) {
      this._isLoading--
    }
    if (value && this._isLoading === 1) {
      bus.$emit(BusEvent.LOADING_STARTED)
    }
    if (this._isLoading === 0) {
      bus.$emit(BusEvent.LOADING_FINISHED)
    }
  }

  get steps(): StepModel[] {
    return this._steps
  }

  get currentStep(): HpStep {
    return this._currentStep
  }

  set currentStep(value: HpStep) {
    this._currentStep = value
  }

  get currentStepComponentName(): string {
    return this._currentStep.toString()
  }

  get currentStepValid(): boolean {
    return this.getStepModelByHpStep(this._currentStep).isValid
  }

  set currentStepValid(value: boolean) {
    this._currentStepValid = value
  }

  get currentFormValid(): boolean {
    return this._currentFormValid || this.getStepModelByHpStep(this._currentStep).defaultNextValidity
  }

  set currentFormValid(value: boolean) {
    this._currentFormValid = value
  }

  get currentStepDefaultNextValidity(): boolean {
    return this.getStepModelByHpStep(this._currentStep).defaultNextValidity
  }

  get projectSorting(): { column: string; reversed: boolean } {
    return this._projectSorting
  }

  set projectSorting(value: { column: string; reversed: boolean }) {
    this._projectSorting = value
  }

  get currentStepComponent(): any {
    return this._currentStepComponent
  }

  set currentStepComponent(value: any) {
    this._currentStepComponent = value
  }

  get errorLoadingProject(): boolean {
    return this._errorLoadingProject
  }

  set errorLoadingProject(value: boolean) {
    this._errorLoadingProject = value
  }

  get nextStep(): HpStep {
    const nextIndex = this.getStepIndex(this._currentStep) + 1
    return nextIndex < this._steps.length ? this._steps[nextIndex].step : HpStep.EMPTY
  }

  get prevStep(): HpStep {
    return this.prevStepModelByStep(this._currentStep).step
  }

  get firstStep(): HpStep {
    return this._steps[0]?.step ?? HpStep.EMPTY
  }

  get isCurrentStepTheLastStep(): boolean {
    return this._steps[this._steps.length - 1]?.step === this._currentStep
  }

  get isStepLoaded(): boolean {
    return this._currentStep !== HpStep.EMPTY
  }

  public prevStepModelByStep(step: HpStep): StepModel {
    const prevIndex = this.getStepIndex(step) - 1
    return prevIndex >= 0 ? this._steps[prevIndex] : this._steps[0]
  }

  public getStepIndex(step: HpStep): number {
    return this._stepsMap[step.toString()]
  }

  public getLowerStep(step1: HpStep, step2: HpStep): HpStep {
    return this.getStepIndex(step1) < this.getStepIndex(step2) ? step1 : step2
  }

  public isStepGreaterEqual(relativeStep: HpStep, baseStep: HpStep): boolean {
    return this.getStepIndex(relativeStep) >= this.getStepIndex(baseStep)
  }

  public getStepModelByHpStep(step: HpStep): StepModel {
    if (step !== HpStep.EMPTY) {
      return this._steps[this._stepsMap[step.toString()]]
    }
    return {
      name: '',
      title: '',
      step: HpStep.EMPTY,
      isValid: false,
      defaultNextValidity: false,
      tag: ''
    }
  }

  public setCurrentStepValidity(isValid: boolean) {
    this.setStepValidity(this._currentStep, isValid)
  }

  public setStepValidity(step: HpStep, isValid: boolean) {
    this.getStepModelByHpStep(step).isValid = isValid
  }

  public resetSteps() {
    this._steps.forEach(step => (step.isValid = false))
  }

  getCurrentStepTitle(): string {
    return this.getStepModelByHpStep(this._currentStep).title
  }

  getStepName(step: HpStep): string {
    return i18n.global.t(this.getStepModelByHpStep(step).name) || step
  }

  get nextStepName(): string {
    return this.nextStep && i18n.global.t(this.getStepModelByHpStep(this.nextStep).name.replace('steps.', 'nextstep.'))
  }

  get prevStepName() {
    return i18n.global.t(this.getStepModelByHpStep(this.prevStep).name.replace('steps.', 'nextstep.')).toString()
  }
}

const appStore = reactive(new AppStore())
export default appStore
